/**
 * COMMERCE-176B-R1 / COMMERCE-176D-R1 / BANK-IBAN-R3.5 — Customer Message Center
 * One card per order using canonical display state. Cancelled never shows awaiting payment.
 *
 * Lifecycle chip vocabulary (mapped from display_state, not independent duplicate cards):
 * Замовлення прийнято / Order accepted
 * Посилка в дорозі / Parcel on the way
 * Прибуло у відділення / Arrived at branch
 * Чек готовий / Fiscal receipt ready
 *
 * Internal chat: DEFERRED — foundation deferred / відкладено (no second chat SoT).
 */
import { useEffect, useMemo, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  fetchMyOrdersBounded,
  getCachedOrdersList,
  type CustomerOrderSummary,
} from "./order-history-v2.js";
import { LoadingBlock, EmptyState } from "./components.js";
import {
  canonicalDisplayChipUk,
  projectCanonicalDisplayState,
  type CanonicalDisplayState,
} from "./canonical-display-state-v2.js";
import { normalizePaymentTiming } from "./customer-payment-timing-v2.js";

type Props = {
  lang: StoreV2Lang;
  sessionKey: string;
  onOpenOrder: (orderId: string) => void;
  onContactManager: () => void;
};

type Msg = {
  id: string;
  orderId: string;
  displayNumber: string;
  icon: string;
  title: string;
  body: string;
  at: string;
  unread: boolean;
  state: CanonicalDisplayState;
};

const READ_KEY = "asiastyle.store-v2.messages-read.v1";

function readSet(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeSet(ids: Set<string>) {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...ids].slice(-200)));
  } catch {
    /* ignore */
  }
}

function iconFor(state: CanonicalDisplayState): string {
  if (state === "CANCELLED") return "🔴";
  if (state === "PAID" || state === "RECEIVED") return "✅";
  if (state === "IN_TRANSIT" || state === "READY_FOR_PICKUP" || state === "AWAITING_STOCK_CONFIRMATION") return "📦";
  if (state === "PAYMENT_REVIEW" || state === "AWAITING_BANK_CONFIRMATION" || state === "AWAITING_PAYMENT") {
    return "💳";
  }
  if (state === "REFUND_PENDING" || state === "REFUNDED") return "↩️";
  return "📥";
}

function titleFor(lang: StoreV2Lang, state: CanonicalDisplayState): string {
  const uk = canonicalDisplayChipUk(state);
  if (lang !== "en") return uk;
  switch (state) {
    case "CANCELLED":
      return "Cancelled";
    case "REFUND_PENDING":
    case "REFUNDED":
      return "Refund";
    case "RECEIVED":
      return "Received";
    case "READY_FOR_PICKUP":
      return "Ready for pickup";
    case "IN_TRANSIT":
      return "Shipped";
    case "PAID":
      return "Paid";
    case "PAYMENT_REVIEW":
      return "Checking payment";
    case "AWAITING_STOCK_CONFIRMATION":
      return "Waiting for availability confirmation";
    case "AWAITING_BANK_CONFIRMATION":
    case "AWAITING_PAYMENT":
      return "Awaiting payment";
    case "NEW":
    default:
      return "New";
  }
}

export function projectMessages(lang: StoreV2Lang, orders: CustomerOrderSummary[]): Msg[] {
  const out: Msg[] = [];
  for (const o of orders) {
    const orderId = o.order_id;
    const num = o.display_order_number || orderId;
    const timing = (o as { payment_timing?: string | null }).payment_timing ?? null;
    const state = projectCanonicalDisplayState({
      order_top_status: o.order_top_status,
      payment_status: o.payment_status,
      payment_method: o.payment_method,
      ttn_status: o.ttn_status ?? null,
      status_rail: o.status_rail ?? null,
      display_state: (o as { display_state?: string }).display_state ?? null,
      payment_timing: timing,
      bank_confirmed_at: o.bank_confirmed_at ?? null,
    });
    const afterManager = normalizePaymentTiming(timing) === "after_manager_confirmation";
    const unlockedPay =
      afterManager &&
      (state === "AWAITING_PAYMENT" || state === "AWAITING_BANK_CONFIRMATION");
    const total =
      o.total_uah == null || !Number.isFinite(Number(o.total_uah))
        ? null
        : Math.round(Number(o.total_uah) * 100) / 100;
    out.push({
      id: unlockedPay ? `availability:${orderId}` : `order:${orderId}`,
      orderId,
      displayNumber: num,
      icon: unlockedPay ? "✅" : iconFor(state),
      title: unlockedPay
        ? lang === "en"
          ? "✅ Availability confirmed"
          : "✅ Наявність підтверджено"
        : titleFor(lang, state),
      body: unlockedPay
        ? lang === "en"
          ? `Order ${num} is ready to pay.${total != null ? ` Amount due: ${total} ₴` : ""}`
          : `Замовлення ${num} готове до оплати.${total != null ? ` До сплати: ${total} ₴` : ""}`
        : num,
      at: o.created_at,
      unread: true,
      state,
    });
  }
  return out.sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

export function MessagesV2({ lang, sessionKey, onOpenOrder, onContactManager }: Props) {
  const en = lang === "en";
  const [orders, setOrders] = useState<CustomerOrderSummary[]>(
    () => getCachedOrdersList(sessionKey) ?? [],
  );
  const [loading, setLoading] = useState(orders.length === 0);
  const [hardError, setHardError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [readIds, setReadIds] = useState<Set<string>>(() => readSet());

  useEffect(() => {
    let cancelled = false;
    setHardError(false);
    void fetchMyOrdersBounded(sessionKey).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setOrders(res.data);
        setHardError(false);
      } else if (orders.length === 0) {
        setHardError(true);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionKey, attempt]);

  const messages = useMemo(() => projectMessages(lang, orders), [lang, orders]);
  const unread = messages.filter((m) => !readIds.has(m.id)).length;

  function openMsg(m: Msg) {
    const next = new Set(readIds);
    next.add(m.id);
    setReadIds(next);
    writeSet(next);
    onOpenOrder(m.orderId);
  }

  if (loading) return <LoadingBlock label={en ? "Loading…" : "Завантаження…"} />;
  if (hardError) {
    return (
      <div data-testid="sv2-messages-load-error">
        <EmptyState
          emoji="💬"
          title={en ? "Could not load messages." : "Не вдалося завантажити повідомлення."}
        />
        <button
          type="button"
          className="sv2-cta"
          style={{ marginTop: 14 }}
          data-testid="sv2-messages-retry"
          onClick={() => {
            setLoading(true);
            setAttempt((n) => n + 1);
          }}
        >
          {en ? "Try again" : "Спробувати ще раз"}
        </button>
      </div>
    );
  }
  if (messages.length === 0) {
    return (
      <EmptyState
        emoji="💬"
        title={en ? "You have no messages yet." : "У вас поки немає повідомлень."}
        hint={en ? "Order updates will appear here." : "Тут з’являться оновлення замовлень."}
      />
    );
  }

  return (
    <div className="sv2-body" data-testid="sv2-messages">
      {unread > 0 ? (
        <p className="sv2-muted" data-testid="sv2-messages-unread">
          {en ? `${unread} unread` : `${unread} непрочитаних`}
        </p>
      ) : null}
      {messages.map((m) => (
        <button
          key={m.id}
          type="button"
          className="sv2-info-card sv2-cabinet-card"
          data-testid="sv2-message-card"
          data-display-state={m.state}
          onClick={() => openMsg(m)}
          style={{ width: "100%", textAlign: "left", marginBottom: 8 }}
        >
          <strong>
            {m.icon} {m.title}
          </strong>
          <div className="sv2-muted">{m.body}</div>
        </button>
      ))}
    </div>
  );
}
